import { Descriptions, Row } from "antd";
import React from "react";
import DisplayTag from "../../components/DisplayTag";
import {
  AssociatedAddress,
  AssociatedEmail,
  AssociatedPhone,
  Opportunity,
  OpportunityProspectTypes,
} from "../../helpers/shared-interfaces";
import { capitalizeFirstLetter } from "../../helpers/utilities";
import { CompanyContactPerson } from "./CompanyDetails";

interface Props {
  opportunity: Opportunity;
}

export default class OpportunityPreview extends React.Component<
  Props,
  Record<string, unknown>
> {
  render(): JSX.Element {
    const { opportunity } = this.props;
    const { customer, company } = opportunity;

    return (
      <div
        style={{
          marginBottom: "18px",
        }}
      >
        {opportunity.opportunityProspect &&
          opportunity.opportunityProspect.name ===
            OpportunityProspectTypes.retailClient && (
            <div>
              {customer && (
                <Descriptions title="Customer Info">
                  <Descriptions.Item label={<b>Name</b>} key="name">
                    {customer.firstName} {customer.middleName}{" "}
                    {customer.lastName}
                  </Descriptions.Item>
                  <Descriptions.Item label={<b>Gender</b>} key="gender">
                    <span
                      style={{
                        textTransform: "capitalize",
                      }}
                    >
                      {customer.gender}
                    </span>
                  </Descriptions.Item>
                  {customer.ssnitNumber && (
                    <Descriptions.Item
                      label={<b>SSNIT Number</b>}
                      key="ssnitNumber"
                    >
                      {customer.ssnitNumber}
                    </Descriptions.Item>
                  )}
                  {customer.dateOfBirth && (
                    <Descriptions.Item
                      label={<b>Date of Birth</b>}
                      key="dateOfBirth"
                    >
                      {customer.dateOfBirth}
                    </Descriptions.Item>
                  )}
                  {customer.maritalStatus && (
                    <Descriptions.Item
                      label={<b>Marital Status</b>}
                      key="maritalStatus"
                    >
                      {customer.maritalStatus.name &&
                        capitalizeFirstLetter(customer.maritalStatus.name)}
                    </Descriptions.Item>
                  )}

                  {customer.idType && (
                    <Descriptions.Item label={<b>ID Type</b>} key="photoIdType">
                      {capitalizeFirstLetter(customer.idType.name)}
                    </Descriptions.Item>
                  )}

                  {customer.photoIdNumber && (
                    <Descriptions.Item
                      label={<b>ID Number</b>}
                      key="photoIdNumber"
                    >
                      {capitalizeFirstLetter(customer.photoIdNumber)}
                    </Descriptions.Item>
                  )}

                  {opportunity.opportunitySource &&
                    opportunity.opportunitySource.name === "Referral" && (
                      <Descriptions.Item label={<b>Referrer</b>} key="referrer">
                        {opportunity.referrer && opportunity.referrer.name}
                      </Descriptions.Item>
                    )}

                  <Descriptions.Item
                    label={<b>Nationality</b>}
                    key="nationality"
                  >
                    {customer.country && customer.country.name}
                  </Descriptions.Item>

                  {customer &&
                    customer.customerAddresses.map(
                      (
                        customerAddress: AssociatedAddress,
                        index: number,
                      ): JSX.Element => {
                        return (
                          <Descriptions.Item
                            label={<b>{`Address ${index + 1}`}</b>}
                            key={`Address ${index + 1}`}
                          >
                            {customerAddress.isPrimary && <DisplayTag />}{" "}
                            {customerAddress.address.streetName},{" "}
                            {customerAddress.address.poBox},{" "}
                            {customerAddress.address.ghPost},{" "}
                            {customerAddress.address.region},{" "}
                            {customerAddress.address.city},{" "}
                            {customerAddress.address.country.name}
                          </Descriptions.Item>
                        );
                      },
                    )}
                  {customer &&
                    customer.customerEmails?.map(
                      (
                        customerEmail: AssociatedEmail,
                        index: number,
                      ): JSX.Element => {
                        return (
                          <Descriptions.Item
                            label={<b>{`Email ${index + 1}`}</b>}
                            key={customerEmail.id}
                          >
                            {customerEmail.isPrimary && <DisplayTag />}
                            {customerEmail.email.address}
                          </Descriptions.Item>
                        );
                      },
                    )}
                  {customer &&
                    customer.activeCustomerPhones.map(
                      (
                        customerPhone: AssociatedPhone,
                        index: number,
                      ): JSX.Element => {
                        return (
                          <Descriptions.Item
                            label={<b>{`Phone ${index + 1}`}</b>}
                            key={customerPhone.id}
                          >
                            {customerPhone.isPrimary && <DisplayTag />}
                            <DisplayTag type={customerPhone.phone.type} />
                            {customerPhone.phone.number}
                          </Descriptions.Item>
                        );
                      },
                    )}
                </Descriptions>
              )}
            </div>
          )}
        {opportunity.opportunityProspect &&
          opportunity.opportunityProspect.name ===
            OpportunityProspectTypes.institutionalClient && (
            <div>
              {company && (
                <Descriptions title="Client Info" style={{ margin: "20px 0" }}>
                  <Descriptions.Item label={<b>Name</b>} key="name">
                    {company.name}
                  </Descriptions.Item>

                  {company.petraId && (
                    <Descriptions.Item label={<b>Petra Id</b>} key="petraId">
                      {company.petraId}
                    </Descriptions.Item>
                  )}

                  {company.tinNumber && (
                    <Descriptions.Item label={<b>TIN</b>} key="tinNumber">
                      {company.tinNumber}
                    </Descriptions.Item>
                  )}

                  {company.industrySector && (
                    <Descriptions.Item
                      label={<b>Industry Sector</b>}
                      key="industrySector"
                    >
                      {company.industrySector.name}
                    </Descriptions.Item>
                  )}

                  {company.trustee && (
                    <Descriptions.Item label={<b>Trustee</b>} key="trustee">
                      {company.trustee.name}
                    </Descriptions.Item>
                  )}

                  {opportunity.opportunitySource &&
                    opportunity.opportunitySource.name === "Referral" && (
                      <Descriptions.Item label={<b>Referrer</b>} key="referrer">
                        {opportunity.referrer && opportunity.referrer.name}
                      </Descriptions.Item>
                    )}

                  {company &&
                    company.companyAddresses.map(
                      (
                        address: AssociatedAddress,
                        index: number,
                      ): JSX.Element => {
                        return (
                          <Descriptions.Item
                            label={<b>{`Address ${index + 1}`}</b>}
                            key={address.id}
                          >
                            {address.isPrimary && <DisplayTag />}
                            {address.address.streetName},{" "}
                            {address.address.poBox}, {address.address.city},{" "}
                            {address.address.region},{" "}
                            {address.address.country.name}
                          </Descriptions.Item>
                        );
                      },
                    )}
                  {company &&
                    company.companyEmails.map(
                      (email: AssociatedEmail, index: number): JSX.Element => {
                        return (
                          <Descriptions.Item
                            label={<b>{`Email ${index + 1}`}</b>}
                            key={email.id}
                          >
                            {email.isPrimary && <DisplayTag />}
                            {email.email.address}
                          </Descriptions.Item>
                        );
                      },
                    )}
                  {company &&
                    company.companyPhones.map(
                      (phone: AssociatedPhone, index: number): JSX.Element => {
                        return (
                          <Descriptions.Item
                            label={<b>{`Phone ${index + 1}`}</b>}
                            key={phone.id}
                          >
                            {phone.isPrimary && <DisplayTag />}
                            <DisplayTag type={phone.phone.type} />
                            {phone.phone.number}
                          </Descriptions.Item>
                        );
                      },
                    )}
                </Descriptions>
              )}
            </div>
          )}
        {opportunity.opportunityProspect &&
          opportunity.opportunityProspect.name ===
            OpportunityProspectTypes.institutionalClient && (
            <div>
              {company && company?.companyContacts && (
                <div>
                  {company?.companyContacts?.length > 0 && (
                    <Row gutter={[24, 16]}>
                      {company?.companyContacts?.map((contactPersonItem) => {
                        return (
                          <>
                            {CompanyContactPerson(contactPersonItem, undefined)}
                          </>
                        );
                      })}
                    </Row>
                  )}
                </div>
              )}
            </div>
          )}
      </div>
    );
  }
}
